import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCollection } from '../hooks/useFirestore';
import { query as fbQuery, orderBy as fbOrderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

interface AdminUser {
  id: string;
  email: string;
  role: 'admin' | 'operator';
  createdAt: Date;
}

export default function UsersPage() {
  const { data: users } = useCollection<AdminUser>(
    'adminUsers',
    (q) => fbQuery(q, fbOrderBy('createdAt', 'desc'))
  );

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'operator' as 'admin' | 'operator',
  });

  const handleAdd = async () => {
    try {
      // Firebase Authでユーザーを作成
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Firestoreにユーザー情報を保存
      await addDoc(collection(db, 'adminUsers'), {
        uid: userCredential.user.uid,
        email: formData.email,
        role: formData.role,
        createdAt: serverTimestamp(),
      });

      setShowAddDialog(false);
      setFormData({
        email: '',
        password: '',
        role: 'operator',
      });
    } catch (error) {
      console.error('Failed to add user:', error);
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;

    try {
      await updateDoc(doc(db, 'adminUsers', selectedUser.id), {
        role: formData.role,
        updatedAt: serverTimestamp(),
      });
      setShowEditDialog(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('このユーザーを削除してもよろしいですか？')) return;

    try {
      await deleteDoc(doc(db, 'adminUsers', userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">ユーザー管理</Typography>
        <Button
          variant="contained"
          onClick={() => setShowAddDialog(true)}
        >
          新規作成
        </Button>
      </Box>

      <Card>
        <List>
          {users?.map((user) => (
            <ListItem
              key={user.id}
              secondaryAction={
                <Box>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setSelectedUser(user);
                      setFormData({
                        ...formData,
                        email: user.email,
                        role: user.role,
                      });
                      setShowEditDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(user.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={user.email}
                secondary={user.role === 'admin' ? '管理者' : '運営'}
              />
            </ListItem>
          ))}
        </List>
      </Card>

      {/* 新規作成ダイアログ */}
      <Dialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>新規ユーザー作成</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="メールアドレス"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="パスワード"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>権限</InputLabel>
            <Select
              value={formData.role}
              label="権限"
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'operator' })}
            >
              <MenuItem value="admin">管理者</MenuItem>
              <MenuItem value="operator">運営</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>キャンセル</Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={!formData.email || !formData.password}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 編集ダイアログ */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ユーザーを編集</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="メールアドレス"
            value={formData.email}
            disabled
            sx={{ mt: 2, mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>権限</InputLabel>
            <Select
              value={formData.role}
              label="権限"
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'operator' })}
            >
              <MenuItem value="admin">管理者</MenuItem>
              <MenuItem value="operator">運営</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>キャンセル</Button>
          <Button
            onClick={handleEdit}
            variant="contained"
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}